import { useState, useRef, useCallback, useEffect } from 'react';

interface Position {
  x: number;
  y: number;
}

interface UseDraggableReturn {
  position: Position;
  isDragging: boolean;
  dragRef: React.RefObject<HTMLElement>;
  handleRef: React.RefObject<HTMLElement>;
}

export const useDraggable = (initialPosition?: Position): UseDraggableReturn => {
  const [position, setPosition] = useState<Position>(initialPosition || { x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [mouseStart, setMouseStart] = useState<Position>({ x: 0, y: 0 });
  const [elementStart, setElementStart] = useState<Position>({ x: 0, y: 0 });
  
  const dragRef = useRef<HTMLElement>(null);
  const handleRef = useRef<HTMLElement>(null);

  const handleMouseDown = useCallback((e: MouseEvent) => {
    if (!dragRef.current || !handleRef.current) return;
    
    // Store initial mouse position
    setMouseStart({ x: e.clientX, y: e.clientY });
    
    // Store current element position
    setElementStart({ x: position.x, y: position.y });
    
    // Start listening for drag (but don't set isDragging yet)
    setIsDragging(true);
    
    // Prevent event propagation and default behavior
    e.preventDefault();
    e.stopPropagation();
    e.stopImmediatePropagation();
    
    // Prevent text selection
    document.body.style.userSelect = 'none';
  }, [position]);

  const handleMouseMove = useCallback((e: MouseEvent) => {
    if (!isDragging || !dragRef.current) return;

    // Prevent event propagation during drag
    e.preventDefault();
    e.stopPropagation();
    e.stopImmediatePropagation();

    // Add dragging class to body on first movement
    if (!document.body.classList.contains('dragging-panel')) {
      document.body.classList.add('dragging-panel');
      document.body.style.pointerEvents = 'none';
      if (dragRef.current) {
        dragRef.current.style.pointerEvents = 'auto';
      }
    }

    // Calculate mouse movement delta
    const deltaX = e.clientX - mouseStart.x;
    const deltaY = e.clientY - mouseStart.y;
    
    // Calculate new position from initial element position + mouse delta
    const newX = elementStart.x + deltaX;
    const newY = elementStart.y + deltaY;
    
    // Keep panel within viewport bounds
    const windowWidth = window.innerWidth;
    const windowHeight = window.innerHeight;
    const rect = dragRef.current.getBoundingClientRect();
    
    const clampedX = Math.max(0, Math.min(windowWidth - rect.width, newX));
    const clampedY = Math.max(0, Math.min(windowHeight - rect.height, newY));
    
    setPosition({ x: clampedX, y: clampedY });
  }, [isDragging, mouseStart, elementStart]);

  const handleMouseUp = useCallback(() => {
    setIsDragging(false);
    
    // Remove dragging class from body
    document.body.classList.remove('dragging-panel');
    
    // Restore normal pointer events and selection
    document.body.style.userSelect = '';
    document.body.style.pointerEvents = '';
    
    // Reset drag element pointer events
    if (dragRef.current) {
      dragRef.current.style.pointerEvents = '';
    }
  }, []);

  useEffect(() => {
    const handle = handleRef.current;
    if (!handle) return;

    handle.addEventListener('mousedown', handleMouseDown);
    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);

    return () => {
      handle.removeEventListener('mousedown', handleMouseDown);
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [handleMouseDown, handleMouseMove, handleMouseUp]);

  return {
    position,
    isDragging,
    dragRef,
    handleRef
  };
};